import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Spinner, Alert, Badge, Container, Form, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/Dashboard.css';
import apiService from '../services/apiService';
import OrderDetailsTable from '../components/OrderDetailsTable';
import Breadcrumb from '../components/Breadcrumb';

const ClosingOfTheDay = () => {
  const [estimatedClosingData, setEstimatedClosingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('today');

  // Filter options for status filtering
  const statusFilterOptions = [
    { value: 'all', label: 'All Orders', description: 'All estimated closing orders' },
    { value: 'OPEN', label: 'Open Orders', description: 'Orders that are still open' },
    { value: 'CLOSED', label: 'Closed Orders', description: 'Orders that have been closed' }
  ];

  // Date range filter options
  const dateRangeOptions = [
    { value: 'today', label: 'Today', description: 'Today only' },
    { value: '7', label: '7 Days', description: 'Next 7 days from today' },
    { value: '15', label: '15 Days', description: 'Next 15 days from today' },
    { value: '30', label: '30 Days', description: 'Next 30 days from today' }
  ];

  // Calculate start and end dates based on selected range
  const getDateRange = (range) => {
    const today = new Date();
    const startDate = today.toISOString().split('T')[0];
    
    if (range === 'today') {
      return { startDate, endDate: startDate };
    }
    
    const endDate = new Date(today.getTime() + (parseInt(range) * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
    return { startDate, endDate };
  };

  // Handle status filter change
  const handleStatusFilterChange = (filterValue) => {
    setSelectedStatusFilter(filterValue);
  };

  // Handle date range filter change
  const handleDateRangeChange = (rangeValue) => {
    setSelectedDateRange(rangeValue);
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedStatusFilter('all');
    setSelectedDateRange('today');
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  // Function to fetch estimated closing data
  const fetchEstimatedClosingData = useCallback(async () => {
    try {
      setLoading(true);
      
      const { startDate, endDate } = getDateRange(selectedDateRange);
      console.log({selectedDateRange})
      const statusFilter = selectedStatusFilter !== 'all' ? selectedStatusFilter : null;
      
      console.log('Fetching estimated closing data for date range:', { startDate, endDate, statusFilter });
      
      const response = await apiService.getEstimatedClosingData(statusFilter, startDate, endDate);
      console.log('Estimated Closing Data received:', response);
      
      // Map the new API response structure
      const mappedData = {
        order_details: response.data || [],
        count: response.count || 0,
        success: response.success || false
      };
      
      setEstimatedClosingData(mappedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching estimated closing data:', err);
      setError('Failed to load estimated closing data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [selectedDateRange, selectedStatusFilter]);

  useEffect(() => {
    console.log('ClosingOfTheDay component mounted - initializing API calls',);
    fetchEstimatedClosingData();
  }, []);

  // Refetch data when filters change
  useEffect(() => {
    fetchEstimatedClosingData();
  }, [fetchEstimatedClosingData]);



  const breadcrumbItems = [
    { label: 'Agent Copies', href: '#' },
    { label: 'Expected Closing of the Day', href: null }
  ];

  return (
    <Container fluid className="px-4 py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Breadcrumb items={breadcrumbItems} />
      
      {/* Page Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="page-title mb-2">
            Expected Closings
          </h1>
          <p className="page-subtitle mb-0">View and filter expected closing orders</p>
        </div>
        <Button 
          variant={showFilters ? "primary" : "outline-primary"}
          onClick={() => setShowFilters(!showFilters)}
          className="d-flex align-items-center gap-2"
        >
          {showFilters ? 'Filters' : 'Filters'}
                    <i className="fas fa-filter"></i>

        </Button>
      </div>

      {/* Enhanced Professional Filter Section */}
      {showFilters && (
        <Card 
          className="mb-4 filter-card" 
          style={{ 
            border: 'none', 
            boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            animation: 'slideDown 0.3s ease-out'
          }}
        >
          <Card.Header 
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '16px 16px 0 0',
              padding: '1rem 1.5rem'
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="fas fa-sliders-h me-2"></i>
                <h6 className="mb-0 fw-bold">Filters</h6>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button 
                  className="btn btn-sm btn-outline-light"
                  onClick={() => setShowFilters(false)}
                  style={{ borderRadius: '6px', padding: '0.25rem 0.5rem' }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </Card.Header>
          <Card.Body style={{ padding: '1rem 1rem 2rem 1rem !important' }}>
            <Row className="align-items-center g-4 mb-2">
             
              <Col lg={3}>
                <div className="filter-controls">
                  <label className="form-label fw-semibold text-muted mb-2">
                    Expected
                  </label>
                  <div className="position-relative">
                    <Form.Select
                      value={selectedDateRange}
                      onChange={(e) => handleDateRangeChange(e.target.value)}
                      className="form-select-modern"
                      style={{
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        fontSize: '0.95rem',
                        padding: '0.75rem 1rem',
                        background: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#667eea';
                        e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e9ecef';
                        e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      }}
                    >
                      {dateRangeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <i className="fas fa-chevron-down position-absolute" 
                       style={{
                         right: '1rem',
                         top: '50%',
                         transform: 'translateY(-50%)',
                         pointerEvents: 'none',
                         color: '#6c757d',
                         fontSize: '0.8rem'
                       }}>
                    </i>
                  </div>
                </div>
              </Col>
            </Row>
            
            {/* Filter Statistics */}
            <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
              <Col>
                <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                  <div className="filter-actions d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                    <button 
                      className="btn btn-outline-primary btn-sm me-2"
                      onClick={() => {
                        setSelectedStatusFilter('all');
                        setSelectedDateRange('today');
                      }}
                      style={{ 
                        borderRadius: '8px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="fas fa-refresh me-1"></i>
                      Reset
                    </button>
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => fetchEstimatedClosingData()}
                      style={{ 
                        borderRadius: '8px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="fas fa-sync-alt me-1"></i>
                      Refresh
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Statistics Summary Cards */}
      {estimatedClosingData && (
        <Row className="mb-4">
        
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100" style={{
              background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
              color: 'white'
            }}>
              <Card.Body className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="text-white-50 small text-uppercase fw-bold mb-1">
                    Open Orders
                  </div>
                  <div className="h4 mb-0 fw-bold">
                    {estimatedClosingData?.order_details ? 
                      estimatedClosingData.order_details.filter(order => order.status !== 'CLOSED').length : 0
                    }
                  </div>
                </div>
                <div className="ms-3">
                  <i className="fas fa-times-circle fa-2x opacity-75"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <Card.Body className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="text-white-50 small text-uppercase fw-bold mb-1">
                    Total Orders
                  </div>
                  <div className="h4 mb-0 fw-bold">
                    {estimatedClosingData?.order_details ? estimatedClosingData.order_details.length : 0}
                  </div>
                </div>
                <div className="ms-3">
                  <i className="fas fa-list fa-2x opacity-75"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100" style={{
              background: 'linear-gradient(135deg, #fd7e14 0%, #e55353 100%)',
              color: 'white'
            }}>
              <Card.Body className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <div className="text-white-50 small text-uppercase fw-bold mb-1">
                    Expected Closing Date
                  </div>
                  <div className="h6 mb-0 fw-bold">
                    {(() => {
                      const { startDate, endDate } = getDateRange(selectedDateRange);
                      const formatDate = (dateStr) => {
                        // Parse date string properly to avoid timezone issues
                        const [year, month, day] = dateStr.split('-');
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        });
                      };
                      
                      if (startDate === endDate) {
                        return formatDate(startDate);
                      }
                      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
                    })()} 
                  </div>
                </div>
                <div className="ms-3">
                  <i className="fas fa-calendar-day fa-2x opacity-75"></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
       
        </Row>
      )}

      {/* Global Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          <Alert.Heading>
            <i className="fas fa-exclamation-triangle me-2"></i>
            Error Loading Data
          </Alert.Heading>
          <p className="mb-0">{error}</p>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading expected closing orders...</p>
        </div>
      )}

      {/* No Data State */}
      {!loading && estimatedClosingData && (!estimatedClosingData.order_details || estimatedClosingData.order_details.length === 0) && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No Closing Orders Found</h5>
            
          </Card.Body>
        </Card>
      )}

      {/* Order Details Table */}
      {!loading && estimatedClosingData && estimatedClosingData.order_details && estimatedClosingData.order_details.length > 0 && (
        
            <OrderDetailsTable 
              orderDetails={estimatedClosingData.order_details}
              title="Expected Orders"
              showCount={false}
            />
          
      )}

      <style jsx>{`
        .page-title {
          color: #2c3e50;
          font-weight: 700;
          font-size: 2rem;
          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .page-subtitle {
          font-size: 1rem;
          color: #6c757d;
        }
        
        .filter-card {
          animation: slideDown 0.3s ease-out;
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .form-control-modern, .form-select-modern {
          border-color: #e0e6ed;
          border-radius: 8px;
          padding: 0.6rem 1rem;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }
        
        .form-control-modern:focus, .form-select-modern:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }
        
        .info-badge {
          transition: all 0.3s ease;
        }
        
        .info-badge:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(23, 162, 184, 0.4) !important;
        }
      `}</style>
    </Container>
  );
};

export default ClosingOfTheDay;